import React from 'react';
import {Button, Card, Col, Modal, Row, Tag, Tooltip} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import ErrorBoundary from '../ErrorBoundary';
import {getMandatoryTagTooltipText} from '../../helpers/contact';
import {ucFirst} from '../../helpers/misc';
import {useDeleteContactMethodMutation, useGetContactMethodsQuery} from '../../store/api/contactMethodsApi';
import {businessAddressUnauthorized} from '../../helpers/text';
import {messageType, showMessages} from '../../helpers/message';
import {BaseError} from '../../types/apiTypes';
import {ContactData} from '../../types/contactMethodTypes';
import AddressFormDrawer from './AddressFormDrawer';
import {useAppSelector} from '../../hooks/useStore';
import {selectCurrentProfile, selectIsStaff} from '../../store/slices/userSlice';

import '../../containers/staff/accounts/contactInfo/ContactInfoPage.less';
import './AddressCard.less';

const {confirm} = Modal;

const typeTagColors: { [index: string]: string } = {
    mailing: 'geekblue',
    billing: 'green',
    home: 'green',
    legal: 'purple',
};

const AddressCard = ({contactableType}: { contactableType: 'profile' | 'company' }) => {
    const isStaff = useAppSelector(selectIsStaff);
    const profile = useAppSelector(selectCurrentProfile);
    const account = profile?.account;
    const contactableID = contactableType === 'profile' ? profile?.id : account?.company_id;

    // Only staff can add/edit addresses on business accounts.
    const disableButtons = !isStaff && contactableType === 'company';
    const addressLabel = contactableType === 'company' ? 'Business Address' : 'Address';

    const {data: contactMethodsData, isLoading} = useGetContactMethodsQuery({id: contactableID,contactableType: contactableType}, {refetchOnMountOrArgChange: true});
    const [deleteContactMethod, {error, isError, isSuccess, data: deleteData}] = useDeleteContactMethodMutation();

    const addresses = contactMethodsData?.address || [];

    const [isDrawerVisible, setDrawerVisible] = React.useState(false);
    const [drawerText, setDrawerText] = React.useState(`Add ${addressLabel}`);
    const [editingAddress, setEditingAddress] = React.useState<ContactData | null>(null);

    React.useEffect(() => {
        if (isSuccess && deleteData) {
            showMessages('Address successfully deleted.', messageType.success);
        } else if (isError && error) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [isSuccess, isError, deleteData, error]);

    const onEdit = (address: ContactData) => {
        setDrawerVisible(true);
        setDrawerText(`Edit ${addressLabel}`);
        setEditingAddress(address);
    };

    const onDelete = (address: ContactData) => {
        const addressID = address.id;

        confirm({
            title: 'Are you sure you want to delete this address?',
            content: address.street_address_1,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: () => deleteContactMethod(addressID),
            onCancel() {},
        });
    };

    const showDrawer = () => {
        setDrawerVisible(true);
        setDrawerText(`Add ${addressLabel}`);
    };

    const renderEditDeleteButtonIcons = (address: ContactData) => {
        if (disableButtons) {
            return (
                <div className="address-edit-delete-icon-container">
                    <EditOutlined
                        key="edit"
                        className="address-edit-delete-icon"
                        onClick={() => showMessages(businessAddressUnauthorized, messageType.error)}
                    />
                    <DeleteOutlined
                        key="delete"
                        className="address-edit-delete-icon"
                        onClick={() => showMessages(businessAddressUnauthorized, messageType.error)}
                    />
                </div>
            )
        }

        return (
            <div className="address-edit-delete-icon-container">
                <EditOutlined className="address-edit-delete-icon" key="edit" onClick={() => onEdit(address)}/>
                {
                    address.hasMandatoryType ?
                        <Tooltip title={getMandatoryTagTooltipText(address.mandatoryTypes, 'delete this address')}>
                            <DeleteOutlined className="anticon-disabled address-edit-delete-icon"/>
                        </Tooltip>
                        :
                        <DeleteOutlined className="address-edit-delete-icon" key="delete" onClick={() => onDelete(address)}/>
                }
            </div>
        )
    };

    const renderCardContent = () => {
        return (
            <>
                {
                    addresses && addresses.length > 0 ?
                        addresses.map((address, index) => (
                            <Col key={index} className="address-card-manage-column">
                                <Card>
                                    <div className="address-card-content">
                                        <div className="address-tags-column">
                                            {
                                                address.type.length !== 0 ?
                                                    address.type.map((type, index) => {
                                                        return (
                                                            <Tag
                                                                className="address-ant-tag"
                                                                color={typeTagColors[type] ?? 'red'}
                                                                key={index}
                                                            >
                                                                {ucFirst(type)}
                                                            </Tag>
                                                        )
                                                    })
                                                    : (
                                                        <Tag className="address-ant-tag" color="red" key={index}>
                                                            Not set
                                                        </Tag>
                                                    )
                                            }
                                        </div>
                                        <Col className="address-card-content-text-container">
                                            <Row className="address-card-content-text">{address.street_address_1}</Row>
                                            <Row className="address-card-content-text">{address.street_address_2}</Row>
                                            <Row className="address-card-content-text">{address.city}, {address.province}</Row>
                                            <Row className="address-card-content-text">{address.postal_code}</Row>
                                            <Row className="address-card-content-text">{address.country}</Row>
                                        </Col>
                                        {renderEditDeleteButtonIcons(address)}
                                    </div>
                                </Card>
                            </Col>
                        )) : null
                }
            </>
        )
    };

    return (
        <Card
            title={addressLabel}
            bordered={true}
            loading={isLoading}
            className="address-card-main"
        >
            <ErrorBoundary>
                <Row gutter={16}>
                    <Button
                        className="add-address-button"
                        type="default"
                        block
                        icon={<PlusOutlined/>}
                        onClick={showDrawer}
                        disabled={disableButtons}
                    >
                        {`Add ${addressLabel}`}
                    </Button>
                    {renderCardContent()}
                </Row>
                <AddressFormDrawer
                    contactableID={contactableID}
                    contactableType={contactableType}
                    drawerText={drawerText}
                    isDrawerVisible={isDrawerVisible}
                    setDrawerVisible={setDrawerVisible}
                    address={editingAddress}
                />
            </ErrorBoundary>
        </Card>
    );
};

export default AddressCard;
